import {
  Component,
  Input,
  Output,
  EventEmitter,
  ViewChild,
  ElementRef,
  AfterViewInit,
  OnChanges,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatRadioModule } from '@angular/material/radio';
import { CustomerInformationFormComponent } from '../customer-information-form/customer-information-form.component';
import { PackageSelectionComponent } from '../package-selection/package-selection.component';
import { AppointmentFormComponent } from '../appointment-form/appointment-form.component';
import { CustomerInfoComponent } from '../customer-info/customer-info.component';
import {
  VehicleListComponent,
  VehicleListData,
  EditVehicleData,
} from '../vehicle-list/vehicle-list.component';
import { DiscardChangesModalComponent } from '../../shared/components/discard-changes-modal/discard-changes-modal.component';
import { DeleteVehicleAlertModalComponent } from '../../shared/components/delete-vehicle-alert-modal/delete-vehicle-alert-modal.component';
import { SuccessModalComponent } from '../success-modal/success-modal.component';
import { AppointmentDetailsModalComponent } from '../appointment-details-modal/appointment-details-modal.component';
import { CancelAppointmentModalComponent } from '../cancel-appointment-modal/cancel-appointment-modal.component';
import { CancellationReasonModalComponent } from '../cancellation-reason-modal/cancellation-reason-modal.component';
import { CancellationSuccessModalComponent } from '../cancellation-success-modal/cancellation-success-modal.component';
import { CancellationErrorModalComponent } from '../cancellation-error-modal/cancellation-error-modal.component';
import { RescheduleAppointmentModalComponent } from '../reschedule-appointment-modal/reschedule-appointment-modal.component';
import { MarkCompleteModalComponent } from '../mark-complete-modal/mark-complete-modal.component';
import { VehicleService } from '../../services/vehicle.service';
import { AppointmentService } from '../../services/appointment.service';
import { AmplitudeService } from '../../services/amplitude.service';
import { NotificationService } from '../../services/notification.service'; // NotificationService;
import { AuthService } from '../../services/auth.service';
import { Appointment } from '../../models/appointment.model';
import { sanitizeErrorMessage } from '../../shared/utils/error.util';
import { subscribeWithStatusAndData } from '../../shared/utils/api-response.util';
import { runVehicleUpdate } from '../../shared/utils/vehicle-edit.util';
import { buildEditingVehicleData } from '../../shared/utils/vehicle-editing-data.util';
import { clearCancellationModalsAndAppointment } from '../../shared/utils/cancellation-modal.util';

@Component({
  selector: 'app-schedule-appointment-modal',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatRadioModule,
    PackageSelectionComponent,
    CustomerInformationFormComponent,
    AppointmentFormComponent,
    CustomerInfoComponent,
    VehicleListComponent,
    DiscardChangesModalComponent,
    DeleteVehicleAlertModalComponent,
    SuccessModalComponent,
    AppointmentDetailsModalComponent,
    CancelAppointmentModalComponent,
    CancellationReasonModalComponent,
    CancellationSuccessModalComponent,
    CancellationErrorModalComponent,
    RescheduleAppointmentModalComponent,
    MarkCompleteModalComponent,
  ],
  templateUrl: './schedule-appointment-modal.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./schedule-appointment-modal.component.scss'],
})
export class ScheduleAppointmentModalComponent implements AfterViewInit, OnChanges {
  @Input() isVisible = false;
  @Output() close = new EventEmitter<void>();
  @Output() appointmentCreated = new EventEmitter<void>();
  @ViewChild('tabMenu', { static: false }) tabMenu!: ElementRef;
  @ViewChild('customerInfoComponent', { static: false })
  customerInfoComponent!: CustomerInfoComponent;

  currentStep = 1;
  showScrollIndicator = false;
  isScrolledToEnd = false;
  step2FormValid = false;
  step3FormValid = false;
  step4FormValid = false;
  customerData: any = null;
  currentCustomerId = ''; // Track current customer ID to detect changes
  /** Form values captured when a customer was selected/created; used to detect post-selection edits. */
  private selectedCustomerFormSnapshot: {
    firstName: string;
    lastName: string;
    phoneNumber: string;
    email: string;
  } | null = null;
  vehicleData: any[] = [];
  isLoadingVehicles = false;
  /** Invalidates in-flight vehicle fetches on reset or newer requests. */
  private vehicleFetchGeneration = 0;
  selectedVehicleIndex = -1;
  selectedVehicle: any = null;

  // Edit mode properties
  isEditingVehicle = false;
  editingVehicleIndex = -1;
  editingVehicleData: EditVehicleData | null = null;

  // Add new vehicle mode properties
  isAddingNewVehicle = false;

  // Delete vehicle modal properties
  showDeleteModal = false;
  vehicleToDelete: any = null;
  vehicleToDeleteIndex = -1;
  isDeletingVehicle = false;

  // Close / cancel confirmation
  showCloseConfirmationModal = false;

  // Success modal properties
  showSuccessModal = false;
  successVehicleMake = '';
  successVehicleModel = '';
  successVehicleYear = '';

  // Appointment details modal properties
  showAppointmentDetailsModal = false;
  createdAppointmentId = '';
  selectedAppointmentForDetails: Appointment | null = null;

  // Cancel appointment flow
  isCancelModalVisible = false;
  isCancellationReasonModalVisible = false;
  isCancellationSuccessModalVisible = false;
  isCancellationErrorModalVisible = false;
  appointmentToCancel: Appointment | null = null;

  // Reschedule/Mark complete flow
  isRescheduleModalVisible = false;
  isRescheduleLoading = false;
  appointmentToReschedule: Appointment | null = null;
  isMarkCompleteModalVisible = false;
  appointmentToMarkComplete: Appointment | null = null;

  // Mobile dropdown properties
  openDropdownIndex = -1;
  isMobile = false;

  // Store form data for persistence
  customerFormData = {
    firstName: '',
    lastName: '',
    phoneNumber: '',
    email: '',
  };

  appointmentFormData = {
    location: '',
    staff: '',
    date: null as Date | null,
    duration: '',
  };

  vehicleFormData: VehicleListData = {
    selectedState: '',
    licensePlate: '',
    vehicleData: null,
    isVehicleFetched: false,
    vehicleId: '',
  };

  // Store package data
  selectedPackageId = '';

  // Store form references
  @ViewChild('vehicleListComponent', { static: false }) vehicleListComponent!: VehicleListComponent;
  @ViewChild('addVehicleComponent', { static: false }) addVehicleComponent!: VehicleListComponent;
  @ViewChild('vehicleDetailsForm', { static: false })
  vehicleDetailsForm!: CustomerInformationFormComponent;

  // Step completion tracking
  completedSteps = new Set<number>(); // No steps are initially completed
  maxReachedStep = 1; // Track the highest step reached

  constructor(
    private vehicleService: VehicleService,
    private appointmentService: AppointmentService,
    private amplitudeService: AmplitudeService,
    private messageService: NotificationService,
    private authService: AuthService
  ) {}

  /**
   * Angular lifecycle hook executed after the component's view has been initialized; checks scrollability and mobile view and, if on step 4 with vehicles present, schedules an update of the step 4 form validity.
   * @example
   * gAfterViewInit()
   * undefined
   * @returns {{void}} No return value.
   **/
  ngAfterViewInit(): void {
    this.checkScrollability();
    this.checkMobileView();

    // Update form validity if we're on step 4 with vehicles
    if (this.currentStep === 4 && this.vehicleData && this.vehicleData.length > 0) {
      // Use a timeout to ensure the view child is properly initialized
      setTimeout(() => {
        this.updateStep4FormValidity();
      }, 0);
    }
  }

  onClose(): void {
    this.close.emit();
  }

  onCancelClick(): void {
    this.showCloseConfirmationModal = true;
  }

  onCloseConfirmationConfirm(): void {
    this.showCloseConfirmationModal = false;
    this.close.emit();
  }

  onCloseConfirmationCancel(): void {
    this.showCloseConfirmationModal = false;
  }

  onBackdropClick(event: MouseEvent): void {
    // Close modal when clicking on backdrop (overlay)
    if (event.target === event.currentTarget) {
      this.onCancelClick();
    }
    // Close any open dropdowns
    this.closeDropdown();
  }

  // Check scrollability when modal becomes visible
  ngOnChanges(): void {
    if (this.isVisible) {
      // Reset modal state when opened
      this.resetModalState();

      // Delay to ensure modal is fully rendered
      setTimeout(() => {
        this.checkScrollability();
      }, 100);
    }
  }

  // Reset vehicle state when customer changes
  /**
   * Reset vehicle-related state properties to their initial default values.
   * @example
   * resetVehicleState()
   * // undefined
   * @returns {void} Resets vehicleData, selection and editing flags, form data, dropdown index, and validation state to their defaults.
   **/
  private resetVehicleState(): void {
    this.vehicleFetchGeneration++;
    this.isLoadingVehicles = false;
    this.vehicleData = [];
    this.selectedVehicleIndex = -1;
    this.selectedVehicle = null;
    this.isEditingVehicle = false;
    this.editingVehicleIndex = -1;
    this.editingVehicleData = null;
    this.isAddingNewVehicle = false;
    this.openDropdownIndex = -1; // Reset mobile dropdown
    this.vehicleFormData = {
      selectedState: '',
      licensePlate: '',
      vehicleData: null,
      isVehicleFetched: false,
      vehicleId: '',
    };
    this.step4FormValid = false;
  }

  // Reset modal state when opened
  /**
   * Reset the modal and form-related component state to its initial default values.
   * @example
   * resetModalState()
   * undefined
   * @returns {{void}} Resets all modal flags, step counters, form data objects, appointment and vehicle-related state to their initial defaults.
   */
  private resetModalState(): void {
    this.currentStep = 1;
    this.maxReachedStep = 1;
    this.completedSteps = new Set(); // No steps are initially completed

    // Reset form data
    this.selectedPackageId = '';
    this.step2FormValid = false;
    this.step3FormValid = false;
    this.step4FormValid = false;
    this.customerData = null;
    this.currentCustomerId = '';
    this.selectedCustomerFormSnapshot = null;
    this.resetVehicleState();

    // Reset appointment details modal state
    this.showAppointmentDetailsModal = false;
    this.createdAppointmentId = '';
    this.selectedAppointmentForDetails = null;
    this.showCloseConfirmationModal = false;
    this.isRescheduleModalVisible = false;
    this.isRescheduleLoading = false;
    this.appointmentToReschedule = null;
    this.isMarkCompleteModalVisible = false;
    this.appointmentToMarkComplete = null;

    // Reset form data objects
    this.customerFormData = {
      firstName: '',
      lastName: '',
      phoneNumber: '',
      email: '',
    };

    this.appointmentFormData = {
      location: '',
      staff: '',
      date: null,
      duration: '',
    };

    this.vehicleFormData = {
      selectedState: '',
      licensePlate: '',
      vehicleData: null,
      isVehicleFetched: false,
      vehicleId: '',
    };

    this.successVehicleMake = '';
    this.successVehicleModel = '';
    this.successVehicleYear = '';
  }

  // Navigation methods with proper validation
  /**
   * Navigate to the specified step in the appointment modal, update component state, and perform step-specific actions such as scrolling, loading vehicle data, and validating step 4.
   * @example
   * oToStep(4)
   * undefined
   * @param {{number}} {{step}} - Target step index to navigate to.
   * @returns {{void}} No return value; updates component state and may trigger asynchronous operations.
   **/
  goToStep(step: number): void {
    if (this.canProceedToStep(step)) {
      this.currentStep = step;

      // Update form validity when navigating to step 4
      if (step === 3) {
        this.scrollTabs();
      }
      if (step === 4) {
        this.scrollTabs();

        // Check if we need to refresh vehicle data when navigating to step 4
        const customerId = this.getCustomerId();
        if (customerId && customerId !== this.currentCustomerId) {
          // Customer has changed, refresh vehicle data

          this.currentCustomerId = customerId;
          this.resetVehicleState();
          this.fetchCustomerVehicles(customerId);
        } else if (customerId && this.vehicleData.length === 0 && !this.isLoadingVehicles) {
          // No vehicles loaded yet, fetch them

          this.fetchCustomerVehicles(customerId);
        } else {
          // Use a timeout to ensure the view child is properly initialized
          setTimeout(() => {
            this.updateStep4FormValidity();
          }, 0);
        }
      }
    }
  }

  nextStep(): void {
    if (this.currentStep < 4 && this.isCurrentStepValid()) {
      // Mark current step as completed before moving to next step
      this.markStepCompleted(this.currentStep);
      this.currentStep += 1;
      this.maxReachedStep = Math.max(this.maxReachedStep, this.currentStep);
    }
  }

  previousStep(): void {
    if (this.currentStep > 1) {
      this.currentStep -= 1;
    }
  }

  // Validation methods for tab navigation
  /**
   * Determine whether navigation to a given step is permitted based on the highest step reached.
   * @example
   * anProceedToStep(3)
   * false
   * @param {number} step - The 1-based step index to check.
   * @returns {boolean} True if the provided step may be accessed; otherwise false.
   **/
  canProceedToStep(step: number): boolean {
    // Always allow access to step 1
    if (step === 1) {
      return true;
    }

    // If we're on the last step (step 4), allow access to any step
    if (this.maxReachedStep === 4) {
      return step >= 1 && step <= 4;
    }

    // Otherwise, only allow access to completed steps and the next available step
    return step <= this.maxReachedStep;
  }

  // Check if current step is valid before allowing next step
  /**
   * Determine whether the current multi-step appointment scheduling step has valid data.
   * @example
   * sCurrentStepValid()
   * true
   * @returns {{boolean}} True if the active step is considered valid based on component state, otherwise false.
   **/
  isCurrentStepValid(): boolean {
    switch (this.currentStep) {
      case 1:
        return Boolean(this.selectedPackageId);
      case 2: {
        const hasAllFields = Boolean(
          this.appointmentFormData.location &&
          this.appointmentFormData.staff &&
          this.appointmentFormData.date &&
          this.appointmentFormData.duration
        );
        const childComponentValid = this.step2FormValid;
        return hasAllFields || childComponentValid;
      }
      case 3: {
        const hasCustomerData = Boolean(this.customerData?.data?.id || this.customerData?.id);
        return hasCustomerData || this.step3FormValid;
      }
      case 4:
        return this.step4FormValid;
      default:
        return false;
    }
  }

  // State management for completed steps
  markStepCompleted(step: number): void {
    this.completedSteps.add(step);
    // Enable next step when current step is completed
    if (step < 4) {
      this.maxReachedStep = Math.max(this.maxReachedStep, step + 1);
    }
  }

  isStepCompleted(step: number): boolean {
    return this.completedSteps.has(step);
  }

  // Scroll functionality
  checkScrollability(): void {
    if (this.tabMenu && this.tabMenu.nativeElement) {
      const element = this.tabMenu.nativeElement;
      this.showScrollIndicator = element.scrollWidth > element.clientWidth;
      this.updateScrollState();
    }
  }

  updateScrollState(): void {
    if (this.tabMenu && this.tabMenu.nativeElement) {
      const element = this.tabMenu.nativeElement;
      const maxScroll = element.scrollWidth - element.clientWidth;
      this.isScrolledToEnd = element.scrollLeft >= maxScroll - 1; // Small tolerance for floating point
    }
  }

  /**
   * Scroll the tab menu horizontally by a fixed step (150px); if already at the end, smoothly reset to the start.
   * @example
   * crollTabs()
   * undefined
   * @returns {void} Does not return a value.
   **/
  scrollTabs(): void {
    if (this.tabMenu && this.tabMenu.nativeElement) {
      const element = this.tabMenu.nativeElement;
      const scrollAmount = 150; // Scroll by 150px

      if (!this.isScrolledToEnd) {
        element.scrollBy({
          left: scrollAmount,
          behavior: 'smooth',
        });

        // Update scroll state after animation
        setTimeout(() => {
          this.updateScrollState();
        }, 300);
      } else {
        // Reset to beginning if at the end
        element.scrollTo({
          left: 0,
          behavior: 'smooth',
        });

        setTimeout(() => {
          this.updateScrollState();
        }, 300);
      }
    }
  }

  onScroll(): void {
    this.updateScrollState();
  }

  /**
   * Continue to the next step in the scheduling flow or finalize appointment creation when on the last step.
   * @example
   * nContinue()
   * // void
   * @returns {void} Validates the current step, advances to the next step and updates the UI, or triggers final appointment creation; shows an error message if validation fails.
   */
  onContinue(): void {
    if (this.currentStep === 4) {
      // For step 4, handle appointment creation
      this.handleFinalAppointmentCreation();
    } else {
      // Check if current step is valid before proceeding
      const isValid = this.isCurrentStepValid();

      // Debug logging

      if (isValid) {
        this.nextStep();
        this.goToStep(this.currentStep);
      } else {
        // Show validation message
        this.messageService.add({
          severity: 'error',
          summary: 'Validation Error',
          detail: sanitizeErrorMessage('Please complete all required fields before proceeding.'),
        });
      }
    }
  }

  onStep3FormValidityChange(isValid: boolean): void {
    this.step3FormValid = isValid;
  }

  onCustomerFormDataChange(formData: any): void {
    this.customerFormData = { ...formData };
    if (
      this.currentCustomerId &&
      this.selectedCustomerFormSnapshot &&
      !this.customerFormMatchesSnapshot(formData, this.selectedCustomerFormSnapshot)
    ) {
      this.currentCustomerId = '';
      this.customerData = null;
      this.selectedCustomerFormSnapshot = null;
      this.resetVehicleState();
    }
  }

  private customerFormMatchesSnapshot(
    formData: { firstName?: string; lastName?: string; phoneNumber?: string; email?: string },
    snapshot: { firstName: string; lastName: string; phoneNumber: string; email: string }
  ): boolean {
    return (
      (formData.firstName || '') === snapshot.firstName &&
      (formData.lastName || '') === snapshot.lastName &&
      (formData.phoneNumber || '') === snapshot.phoneNumber &&
      (formData.email || '') === snapshot.email
    );
  }

  onStep2FormValidityChange(isValid: boolean): void {
    this.step2FormValid = isValid;
  }

  onAppointmentFormDataChange(formData: any): void {
    this.appointmentFormData = { ...formData };
  }

  /**
   * Handle a customer creation/selection response: update internal customer state, reset vehicle state if the customer changed, scroll tabs, and fetch the customer's vehicles (or show an error if no ID). Continues to the next step for create/Continue responses, but not for search selections.
   * @example
   * nCustomerCreated({ data: { id: 'abc123' } })
   * undefined
   * @param {{any}} {{customerResponse}} - Customer response object which may contain the customer id at response.data.id or response.id. Search selections include selectedFromSearch: true.
   * @returns {{void}} No return value; updates component state and triggers side effects (reset, fetch, notifications, optional step advance).
   **/
  onCustomerCreated(customerResponse: any): void {
    // Extract customer ID from response
    let customerId = '';
    if (customerResponse?.data?.id) {
      customerId = customerResponse.data.id;
    } else if (customerResponse?.id) {
      customerId = customerResponse.id;
    }

    // Check if customer has changed
    const customerChanged = customerId && customerId !== this.currentCustomerId;

    // Update customer data and ID
    this.customerData = customerResponse;
    this.currentCustomerId = customerId;
    this.selectedCustomerFormSnapshot = customerId
      ? {
          firstName: this.customerFormData.firstName || '',
          lastName: this.customerFormData.lastName || '',
          phoneNumber: this.customerFormData.phoneNumber || '',
          email: this.customerFormData.email || '',
        }
      : null;

    // If customer changed, reset vehicle-related state
    if (customerChanged) {
      this.resetVehicleState();
    }

    setTimeout(() => {
      this.scrollTabs();
    }, 100);

    if (customerId) {
      // Skip auto-fetching vehicles to prevent auto-filling license plate
      // Users must manually select vehicles during appointment scheduling
      // to avoid incorrect plate assignments
      // this.fetchCustomerVehicles(customerId);

      // Continue/create advances here; search selection stays on the current step.
      if (!customerResponse?.selectedFromSearch) {
        this.proceedToNextStep();
      }
    } else {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: sanitizeErrorMessage('Customer ID not found. Please try again.'),
      });
    }
  }

  /**
   * Fetches vehicles for a given customer and updates component state (loading flag, vehicle list, selected vehicle).
   * @example
   * etchCustomerVehicles('customerId123')
   * undefined
   * @param {{string}} {{customerId}} - Customer ID to fetch vehicles for.
   * @returns {{void}} Does not return a value; updates component state only.
   **/
  fetchCustomerVehicles(customerId: string): void {
    const generation = ++this.vehicleFetchGeneration;
    this.isLoadingVehicles = true;

    // Store the currently selected vehicle ID before refresh
    const previouslySelectedVehicleId = this.selectedVehicle?.id;

    this.vehicleService.getVehiclesByCustomerId(customerId).subscribe({
      next: response => {
        if (generation !== this.vehicleFetchGeneration) {
          return;
        }
        // Ignore stale responses from a previous customer selection
        if (customerId !== this.currentCustomerId) {
          this.isLoadingVehicles = false;
          return;
        }

        this.isLoadingVehicles = false;

        if (response.status && response.data) {
          this.vehicleData = response.data;

          // Validate and adjust selected vehicle after refresh
          this.validateAndAdjustSelectedVehicle(previouslySelectedVehicleId);

          // If no vehicle is selected and vehicles are available, auto-select the first one
          if (this.selectedVehicleIndex === -1 && this.vehicleData.length > 0) {
            this.selectVehicle(0);
          } else if (this.vehicleData.length === 0) {
            // No vehicles available, update form validity
            setTimeout(() => {
              this.updateStep4FormValidity();
            }, 0);
          }
        } else {
          this.vehicleData = [];
          this.selectedVehicleIndex = -1;
          this.selectedVehicle = null;
          // Update form validity when no vehicles
          setTimeout(() => {
            this.updateStep4FormValidity();
          }, 0);
        }

        if (this.currentStep === 4) {
          setTimeout(() => {
            this.updateStep4FormValidity();
          }, 0);
        }
      },
      error: error => {
        if (generation !== this.vehicleFetchGeneration) {
          return;
        }
        if (customerId !== this.currentCustomerId) {
          this.isLoadingVehicles = false;
          return;
        }

        this.isLoadingVehicles = false;
        console.error('Error fetching vehicles:', error);

        this.vehicleData = [];
        this.selectedVehicleIndex = -1;
        this.selectedVehicle = null;
        setTimeout(() => {
          this.updateStep4FormValidity();
        }, 0);
      },
    });
  }

  /**
   * Automatically moves to the next appointment scheduling step if the current step is valid.
   * @example
   * roceedToNextStep()
   * void
   * @returns {{void}} Moves to the next step and updates the current step state; no return value.
   **/
  proceedToNextStep(): void {
    // Automatically move to next step

    if (this.isCurrentStepValid()) {
      this.nextStep();
      this.goToStep(this.currentStep);
    }
  }

  onStep3Continue(): void {
    // This will trigger customer creation through the form
    this.onContinue();
  }

  onCreateCustomer(): void {
    // Existing customer already selected from search — advance on Continue only
    if (this.currentCustomerId) {
      this.proceedToNextStep();
      return;
    }

    // Use ViewChild reference to trigger customer creation
    if (this.customerInfoComponent) {
      this.customerInfoComponent.createCustomer();
    } else {
      console.error('Customer info component not found');
    }
  }

  onStep4FormValidityChange(isValid: boolean): void {
    this.step4FormValid = isValid;
  }

  /**
   * Handle a manually created vehicle: normalize its data, add or select it in the vehicle list, update form state, and refresh customer vehicles.
   * @example
   * nVehicleCreatedManually({ id: '123', plate: 'ABC123', state: 'CA', make: 'Toyota', model: 'Camry', year: 2020, vin: '1HGCM82633A004352' })
   * undefined
   * @param {{any}} {{vehicleData}} - Vehicle data provided when a vehicle is created manually (may include id, plate, state, make, model, year, vin).
   * @returns {{void}} No return value; updates component state and triggers side effects.
   **/
  onVehicleCreatedManually(vehicleData: any): void {
    // Vehicle was created manually with make, model, year
    // Add it to the vehicle list and select it
    if (vehicleData) {
      // Convert VehicleData format to the format expected by vehicle list
      const vehicle = {
        id: vehicleData.id || '',
        license_plate: vehicleData.plate || this.vehicleFormData.licensePlate,
        state: vehicleData.state || this.vehicleFormData.selectedState,
        make: vehicleData.make,
        model: vehicleData.model,
        year: vehicleData.year,
        vin: vehicleData.vin || '',
      };

      // Add to vehicle list if not already present
      const existingIndex = this.vehicleData.findIndex(
        v => v.license_plate === vehicle.license_plate && v.state === vehicle.state
      );

      if (existingIndex === -1) {
        this.vehicleData.push(vehicle);
        this.selectedVehicleIndex = this.vehicleData.length - 1;
        this.selectedVehicle = vehicle;
      } else {
        this.selectedVehicleIndex = existingIndex;
        this.selectedVehicle = this.vehicleData[existingIndex];
      }

      // Update vehicle form data
      this.vehicleFormData.vehicleData = vehicleData;
      this.vehicleFormData.isVehicleFetched = true;
      this.vehicleFormData.vehicleId = vehicle.id;

      // Update form validity to enable Continue button immediately
      // This must be called after setting selectedVehicleIndex and selectedVehicle
      setTimeout(() => {
        this.updateStep4FormValidity();
      }, 0);

      // Refresh vehicle list to ensure consistency (fetchCustomerVehicles will preserve selection)
      const customerId = this.getCustomerId();
      if (customerId) {
        this.fetchCustomerVehicles(customerId);
      }
    }
  }

  onVehicleFormDataChange(formData: VehicleListData): void {
    this.vehicleFormData = { ...formData };
    // Update form validity when vehicle form data changes
    setTimeout(() => {
      this.updateStep4FormValidity();
    }, 0);
  }

  onPackageSelected(packageId: string): void {
    this.selectedPackageId = packageId;
  }

  /**
   * Finalizes appointment creation by validating steps, creating the vehicle if needed, and creating the appointment; resets processing state on validation failure or error and logs errors.
   * @example
   * handleFinalAppointmentCreation()
   * Promise<void>
   * @returns {Promise<void>} Resolves when the appointment creation process completes or the processing state has been reset on failure.
   **/
  private async handleFinalAppointmentCreation(): Promise<void> {
    try {
      // Check if we have all required data
      if (!this.validateAllStepsForAppointment()) {
        // Reset processing state if validation fails
        this.resetProcessingState();
        return;
      }

      // If vehicle doesn't have an ID, create it first
      if (!this.vehicleFormData.vehicleId && this.vehicleFormData.isVehicleFetched) {
        await this.createVehicleFirst();
      }

      // Create the appointment
      await this.createAppointment();
    } catch (error) {
      console.error('Error creating appointment:', error);
      // this.messageService.add({
      //   severity: 'error',
      //   summary: 'Error',
      //   detail: 'Failed to create appointment. Please try again.'
      // });
      // Reset processing state on error
      this.resetProcessingState();
    }
  }

  /**
   * Validate that all required steps for scheduling an appointment are completed; shows user-facing error messages for any missing data.
   * @example
   * this.validateAllStepsForAppointment()
   * false
   * @returns {{boolean}} True if all steps are valid; false if validation failed and an error message was displayed.
   **/
  private validateAllStepsForAppointment(): boolean {
    // Debug logging to see what values we have

    // Validate step 2 (booking details)
    // All fields are required: location, staff, date, duration
    if (
      !this.appointmentFormData.location ||
      !this.appointmentFormData.staff ||
      !this.appointmentFormData.date ||
      !this.appointmentFormData.duration
    ) {
      this.messageService.add({
        severity: 'error',
        summary: 'Missing Data',
        detail: sanitizeErrorMessage(
          'Please complete all booking details (location, staff, date & time, and duration are required).'
        ),
      });
      return false;
    }

    // Validate step 3 (customer)
    if (!this.customerData?.data?.id && !this.customerData?.id) {
      this.messageService.add({
        severity: 'error',
        summary: 'Missing Data',
        detail: sanitizeErrorMessage('Please create a customer first.'),
      });
      return false;
    }

    // Validate step 4 (vehicle)
    if (!this.validateStep4()) {
      this.messageService.add({
        severity: 'error',
        summary: 'Missing Data',
        detail: sanitizeErrorMessage(
          this.vehicleData && this.vehicleData.length > 0
            ? 'Please select a vehicle from the list.'
            : 'Please fetch vehicle details first.'
        ),
      });
      return false;
    }

    return true;
  }

  /**
   * Creates a new vehicle for the currently selected customer by calling the vehicle list component; sets this.vehicleFormData.vehicleId on success and shows error toasts and rejects on failure.
   * @example
   * createVehicleFirst()
   * Promise<void>
   * @returns {Promise<void>} Resolves when a vehicle is successfully created and vehicleFormData.vehicleId is set; rejects and displays error messages if creation fails or required components/IDs are missing.
   **/
  private async createVehicleFirst(): Promise<void> {
    const customerId = this.getCustomerId();
    if (!customerId) {
      throw new Error('Customer ID not found');
    }

    return new Promise((resolve, reject) => {
      if (this.vehicleListComponent) {
        this.vehicleListComponent.createVehicleForCustomer(customerId).subscribe({
          next: response => {
            if (response.status && response.data?.id) {
              this.vehicleFormData.vehicleId = response.data.id;
              resolve();
            } else {
              // Check if response has a message field to show in toast
              const errorMessage = sanitizeErrorMessage(
                response.message || 'Failed to create vehicle'
              );
              this.messageService.add({
                severity: 'error',
                summary: 'Error',
                detail: errorMessage,
              });
              reject(new Error(errorMessage));
            }
          },
          error: error => {
            // Extract error message from API response
            let errorMessage = 'Failed to create vehicle. Please try again.';
            if (error.error?.message) {
              errorMessage = error.error.message;
            } else if (error.error?.error?.message) {
              errorMessage = error.error.error.message;
            } else if (error.message) {
              errorMessage = error.message;
            }

            // Show error message in toast
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: sanitizeErrorMessage(errorMessage),
            });

            reject(error);
          },
        });
      } else {
        reject(new Error('Vehicle component not found'));
      }
    });
  }

  getCustomerId(): string {
    if (this.customerData?.data?.id) {
      return this.customerData.data.id;
    } else if (this.customerData?.customer?.id) {
      return this.customerData.customer.id;
    } else if (this.customerData?.id) {
      return this.customerData.id;
    }
    return '';
  }

  /**
   * Create a new appointment by sending the built payload to the appointment API, handling success and error flows.
   * @example
   * createAppointment()
   * Promise<void>
   * @returns {Promise<void>} Resolves when the appointment is successfully created; rejects with an Error or API error on failure.
   */
  private async createAppointment(): Promise<void> {
    const appointmentPayload = this.buildAppointmentPayload();

    return new Promise((resolve, reject) => {
      this.appointmentService.createAppointment(appointmentPayload).subscribe({
        next: async response => {
          if (response.status) {
            // Track employee assignment in Amplitude
            if (appointmentPayload.employee_id) {
              this.amplitudeService.trackEmployeeAssigned(
                response.data?.id || 'any',
                appointmentPayload.employee_id,
                this.authService.user()?.email || 'any'
              );
            }

            // Store the created appointment ID
            if (response.data?.id) {
              this.createdAppointmentId = response.data.id;
            }

            // Appointment API may return only vehicle_id; fetch full vehicle details for success modal.
            const createdVehicleId = response.data?.vehicle_id || appointmentPayload.vehicle_id;
            await this.populateSuccessVehicleDetails(createdVehicleId);

            // Reset processing state on success
            this.resetProcessingState();
            // Show success modal instead of toast
            this.showSuccessModal = true;
            this.appointmentCreated.emit(); // Emit event to notify parent
            // Close the schedule modal after successful creation.
            this.onClose();
            resolve();
          } else {
            // Show error message from API response
            const errorMessage = sanitizeErrorMessage(
              response.message || 'Failed to create appointment'
            );
            this.messageService.add({
              severity: 'error',
              summary: 'Appointment Creation Failed',
              detail: errorMessage,
            });
            this.resetProcessingState();
            reject(new Error(errorMessage));
          }
        },
        error: error => {
          // Extract error message from API response
          let errorMessage = 'Failed to create appointment. Please try again.';
          if (error.error?.message) {
            errorMessage = error.error.message;
          } else if (error.error?.error?.message) {
            errorMessage = error.error.error.message;
          } else if (error.message) {
            errorMessage = error.message;
          }

          // Show error message
          this.messageService.add({
            severity: 'error',
            summary: 'Appointment Creation Failed',
            detail: sanitizeErrorMessage(errorMessage),
          });

          this.resetProcessingState();
          reject(error);
        },
      });
    });
  }

  /**
   * Populate vehicle details for the success modal using the given vehicle ID or fallback data.
   * @example
   * populateSuccessVehicleDetails('vehicle123')
   * Promise<void>
   * @param {{string}} {{vehicleId}} - Optional vehicle identifier used to fetch and populate vehicle details.
   * @returns {{Promise<void>}} Promise that resolves when the vehicle details have been populated or when the fetch has completed/failed.
   **/
  private async populateSuccessVehicleDetails(vehicleId?: string): Promise<void> {
    const fallbackVehicle = this.selectedVehicle || this.vehicleFormData.vehicleData;
    this.successVehicleMake = fallbackVehicle?.make || '';
    this.successVehicleModel = fallbackVehicle?.model || '';
    this.successVehicleYear = fallbackVehicle?.year ? String(fallbackVehicle.year) : '';

    if (!vehicleId) {
      return;
    }

    return new Promise(resolve => {
      this.vehicleService.getVehicleById(vehicleId).subscribe({
        next: response => {
          if (response.status && response.data) {
            this.successVehicleMake = response.data.make || this.successVehicleMake;
            this.successVehicleModel = response.data.model || this.successVehicleModel;
            this.successVehicleYear = response.data.year
              ? String(response.data.year)
              : this.successVehicleYear;
          }
          resolve();
        },
        error: error => {
          console.error('Error fetching vehicle details for success modal:', error);
          resolve();
        },
      });
    });
  }

  /**
   * Build an appointment payload object from the component's form data and selected items.
   * @example
   * buildAppointmentPayload()
   * { employee_id: 123, customer_id: 456, vehicle_id: 'vehicle_1', package_id: 789, location_id: 10, appointment_time: '2026-03-17T12:00:00.000Z', source: 'web', duration: 60 }
   * @param {{void}} {{none}} - No arguments; reads values from component state (this.appointmentFormData, this.selectedVehicle, this.selectedPackageId, etc.).
   * @returns {{any}} Return appointment payload object ready to be sent to the backend.
   **/
  private buildAppointmentPayload(): any {
    // Convert appointment date to ISO string
    const appointmentTime = this.appointmentFormData.date
      ? this.appointmentFormData.date.toISOString()
      : new Date().toISOString();

    // Use selected vehicle ID if available, otherwise use form vehicle ID
    const vehicleId = this.selectedVehicle?.id || this.vehicleFormData.vehicleId;

    const payload = {
      employee_id: this.appointmentFormData.staff, // Required staff ID
      customer_id: this.getCustomerId(),
      vehicle_id: vehicleId,
      package_id: this.selectedPackageId,
      location_id: this.appointmentFormData.location,
      appointment_time: appointmentTime,
      source: 'web',
      duration: this.appointmentFormData.duration, // Required duration
    };

    return payload;
  }

  // Vehicle selection methods
  /**
   * Toggle selection of a vehicle by index; selects the vehicle or deselects if the same index is clicked, then updates step 4 form validity.
   * @example
   * electVehicle(1)
   * undefined
   * @param {{number}} {{index}} - Index of the vehicle to select; clicking the currently selected index will deselect it.
   * @returns {{void}} No return value.
   **/
  selectVehicle(index: number): void {
    // Allow deselection if clicking the same vehicle
    if (this.selectedVehicleIndex === index) {
      this.selectedVehicleIndex = -1;
      this.selectedVehicle = null;
    } else {
      this.selectedVehicleIndex = index;
      this.selectedVehicle = this.vehicleData[index];
    }

    // Update step 4 form validity based on vehicle selection
    this.updateStep4FormValidity();
  }

  // New method for radio button selection (no deselection allowed)
  onVehicleSelectionChange(index: number): void {
    this.selectedVehicleIndex = index;
    this.selectedVehicle = this.vehicleData[index];

    // Update step 4 form validity based on vehicle selection
    this.updateStep4FormValidity();
  }

  // Method to update step 4 form validity
  /**
   * Update step 4 form validity based on selected vehicle or fetched vehicle data and notify listeners.
   * @example
   * updateStep4FormValidity()
   * undefined
   * @returns {void} No return value; updates internal state and notifies parent/child components about the current validity.
   */
  private updateStep4FormValidity(): void {
    let isValid = false;

    if (this.vehicleData && this.vehicleData.length > 0) {
      // When vehicles are available, check if one is selected
      isValid = this.selectedVehicleIndex >= 0 && this.selectedVehicle !== null;
    } else {
      // When no vehicles available, use existing vehicle form validation
      isValid = this.vehicleFormData.isVehicleFetched;
    }

    this.step4FormValid = isValid;
    this.onStep4FormValidityChange(isValid);

    // Directly notify the vehicle details form component
    if (this.vehicleDetailsForm) {
      this.vehicleDetailsForm.onFormValidityChange(isValid);
    }
  }

  // Method to automatically select the next available vehicle
  /**
   * Automatically selects the next available vehicle after a deletion or adjusts the selection when out of bounds; clears selection if no vehicles.
   * @example
   * autoSelectNextVehicle()
   * undefined
   * @returns {void} No return value; updates the component's selectedVehicle and selectedVehicleIndex state.
   **/
  private autoSelectNextVehicle(): void {
    if (this.vehicleData && this.vehicleData.length > 0) {
      // When a vehicle is deleted, select the vehicle that took its place
      // (same index, which is now the next vehicle), or first vehicle if index is out of bounds
      let nextIndex = this.selectedVehicleIndex;

      // If index is out of bounds, select the last available vehicle
      if (nextIndex >= this.vehicleData.length) {
        nextIndex = this.vehicleData.length - 1;
      }

      // Ensure index is valid
      if (nextIndex >= 0 && nextIndex < this.vehicleData.length) {
        this.selectVehicle(nextIndex);
      } else {
        // Fallback to first vehicle if index is invalid
        this.selectVehicle(0);
      }
    } else {
      // No vehicles available, clear selection
      this.selectedVehicleIndex = -1;
      this.selectedVehicle = null;
    }
  }

  // Method to validate and adjust selected vehicle after vehicle list changes
  /**
   * Ensure the selected vehicle index and reference are valid after vehicle list changes; attempt to restore a previously selected vehicle or auto-select a suitable vehicle.
   * @example
   * validateAndAdjustSelectedVehicle('vehicle-123')
   * undefined
   * @param {{string}} {{previouslySelectedVehicleId}} - Optional ID of the previously selected vehicle to try to restore from the updated vehicle list.
   * @returns {{void}} No return value; updates component state (selectedVehicleIndex and selectedVehicle) as needed.
   **/
  private validateAndAdjustSelectedVehicle(previouslySelectedVehicleId?: string): void {
    // If we have a previously selected vehicle ID, try to find it in the new list
    if (previouslySelectedVehicleId && this.vehicleData && this.vehicleData.length > 0) {
      const foundIndex = this.vehicleData.findIndex(v => v.id === previouslySelectedVehicleId);
      if (foundIndex >= 0) {
        // Vehicle still exists, maintain selection
        this.selectedVehicleIndex = foundIndex;
        this.selectedVehicle = this.vehicleData[foundIndex];
        return;
      }
    }

    // If selected vehicle index is out of bounds or vehicle doesn't exist, auto-select next
    if (this.selectedVehicleIndex >= 0) {
      if (
        this.selectedVehicleIndex >= this.vehicleData.length ||
        !this.vehicleData[this.selectedVehicleIndex] ||
        (previouslySelectedVehicleId && this.selectedVehicle?.id !== previouslySelectedVehicleId)
      ) {
        // Selected vehicle no longer exists or changed, select next available
        this.autoSelectNextVehicle();
      } else {
        // Update selected vehicle reference to ensure it's in sync
        this.selectedVehicle = this.vehicleData[this.selectedVehicleIndex];
      }
    } else if (this.vehicleData && this.vehicleData.length > 0) {
      // No vehicle selected but vehicles are available, select first one
      this.autoSelectNextVehicle();
    }
  }

  editVehicle(vehicle: any, index: number): void {
    this.editingVehicleData = buildEditingVehicleData(vehicle);
    this.editingVehicleIndex = index;
    this.isEditingVehicle = true;
  }

  deleteVehicle(vehicle: any, index: number): void {
    this.vehicleToDelete = vehicle;
    this.vehicleToDeleteIndex = index;
    this.showDeleteModal = true;
  }

  onDeleteModalClose(): void {
    this.showDeleteModal = false;
    this.vehicleToDelete = null;
    this.vehicleToDeleteIndex = -1;
    this.isDeletingVehicle = false;
  }

  /**
   * Handle confirmation of vehicle deletion from the delete modal: validates the vehicle, calls the vehicle service to delete it, updates local vehicle list and selection, updates form validity, and displays success or error messages.
   * @example
   * nDeleteModalConfirm({ id: 'vehicle123' })
   * void
   * @param {{any}} {{vehicle}} - Vehicle object containing at minimum an id used to perform deletion.
   * @returns {{void}} No return value; performs deletion and updates component state.
   **/
  onDeleteModalConfirm(vehicle: any): void {
    if (!vehicle || !vehicle.id) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: sanitizeErrorMessage('Vehicle ID is missing. Cannot delete vehicle.'),
      });
      this.onDeleteModalClose();
      return;
    }

    this.isDeletingVehicle = true;

    this.vehicleService.deleteVehicle(vehicle.id).subscribe({
      next: response => {
        this.isDeletingVehicle = false;

        if (response.status) {
          // Remove the vehicle from the local list
          if (this.vehicleData && this.vehicleToDeleteIndex >= 0) {
            const wasSelectedVehicle = this.selectedVehicleIndex === this.vehicleToDeleteIndex;

            this.vehicleData.splice(this.vehicleToDeleteIndex, 1);

            // Update selected vehicle if it was the deleted one
            if (wasSelectedVehicle) {
              // Automatically select the next vehicle (or first vehicle if deleted was last)
              this.autoSelectNextVehicle();
            } else if (this.selectedVehicleIndex > this.vehicleToDeleteIndex) {
              // Adjust the selected index if it was after the deleted vehicle
              this.selectedVehicleIndex--;
              // Update selected vehicle reference
              if (this.selectedVehicleIndex >= 0 && this.vehicleData[this.selectedVehicleIndex]) {
                this.selectedVehicle = this.vehicleData[this.selectedVehicleIndex];
              }
            }

            // Update form validity after vehicle deletion
            this.updateStep4FormValidity();
          }

          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: 'Vehicle deleted successfully!',
          });

          this.onDeleteModalClose();
        } else {
          this.messageService.add({
            severity: 'error',
            summary: 'Delete Failed',
            detail: sanitizeErrorMessage(
              response.message || 'Failed to delete vehicle. Please try again.'
            ),
          });
        }
      },
      error: error => {
        this.isDeletingVehicle = false;
        console.error('Error deleting vehicle:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: sanitizeErrorMessage(
            'An error occurred while deleting the vehicle. Please try again.'
          ),
        });
      },
    });
  }

  // Override validation for step 4 when vehicles are available
  private validateStep4(): boolean {
    if (this.vehicleData && this.vehicleData.length > 0) {
      return this.selectedVehicleIndex >= 0 && this.selectedVehicle !== null;
    }
    return this.vehicleFormData.isVehicleFetched;
  }

  // Edit mode handlers
  onCancelEdit(): void {
    this.isEditingVehicle = false;
    this.editingVehicleIndex = -1;
    this.editingVehicleData = null;
  }

  /**
   * Save edits to a vehicle by sending an update request, updating local vehicle state, showing a success message, and refreshing the vehicle list.
   * @example
   * nSaveEdit({ licensePlate: 'ABC123', state: 'CA', vehicleData: { vin: '1HGBH41JXMN109186', make: 'Honda', model: 'Civic', year: 2019 } })
   * undefined
   * @param {{EditVehicleData}} {{updatedData}} - Updated vehicle data used to perform the update request and apply changes to local state.
   * @returns {{void}} No return value.
   **/
  onSaveEdit(updatedData: EditVehicleData): void {
    if (!this.editingVehicleData || this.editingVehicleIndex < 0) {
      return;
    }
    runVehicleUpdate(this.vehicleService, updatedData, this.messageService, {
      onSuccess: () => {
        if (this.vehicleData && this.vehicleData[this.editingVehicleIndex]) {
          this.vehicleData[this.editingVehicleIndex] = {
            ...this.vehicleData[this.editingVehicleIndex],
            license_plate: updatedData.licensePlate,
            state: updatedData.state,
            vin: updatedData.vehicleData.vin,
            make: updatedData.vehicleData.make,
            model: updatedData.vehicleData.model,
            year: updatedData.vehicleData.year,
          };
          if (this.selectedVehicleIndex === this.editingVehicleIndex) {
            this.selectedVehicle = this.vehicleData[this.editingVehicleIndex];
          }
        }
        this.messageService.add({
          severity: 'success',
          summary: 'Vehicles details updated!',
          detail: 'The vehicle details have been updated successfully.',
        });
        this.onCancelEdit();
        const customerId = this.getCustomerId();
        if (customerId) {
          this.fetchCustomerVehicles(customerId);
        }
      },
    });
  }

  // Add new vehicle functionality
  onAddNewVehicle(): void {
    this.isAddingNewVehicle = true;
    this.isEditingVehicle = false;
    this.editingVehicleData = null;
    this.editingVehicleIndex = -1;
  }

  onCancelAddVehicle(): void {
    this.isAddingNewVehicle = false;
  }

  /**
   * Handle the result of creating a new vehicle: add it to the list, auto-select it, exit add mode, and refresh customer vehicles.
   * @example
   * nSaveNewVehicle({ success: true, vehicleData: { id: 'v1', make: 'Toyota' } })
   * undefined
   * @param {{any}} {{result}} - Result object from the vehicle creation child component; expected to contain a boolean `success` and `vehicleData` on success.
   * @returns {{void}} No return value; updates component state and triggers side effects.
   **/
  onSaveNewVehicle(result: any): void {
    if (result && result.success) {
      // Vehicle was created successfully
      const newVehicle = result.vehicleData;

      // Add the new vehicle to the vehicle list
      this.vehicleData.push(newVehicle);

      // Auto-select the new vehicle
      this.selectVehicle(this.vehicleData.length - 1);

      // Exit add mode
      this.isAddingNewVehicle = false;

      // Refresh the vehicle list to ensure consistency
      const customerId = this.getCustomerId();
      if (customerId) {
        this.fetchCustomerVehicles(customerId);
      }
    } else {
      // Vehicle creation failed - error message already shown by child component
      console.error('Vehicle creation failed:', result?.error);
    }
  }

  // Success modal methods
  onSuccessModalClose(): void {
    this.showSuccessModal = false;
    this.onClose(); // Close the main modal
  }

  /**
   * Hide the success modal and load appointment details if available, showing the appointment details modal on success or closing otherwise.
   * @example
   * nSuccessModalAction()
   * undefined
   * @returns {void} No return value.
   **/
  onSuccessModalAction(): void {
    this.showSuccessModal = false;

    // Load appointment details and show the appointment details modal
    if (this.createdAppointmentId) {
      subscribeWithStatusAndData(
        this.appointmentService.getAppointmentById(this.createdAppointmentId),
        this.messageService,
        {
          errorDetail: 'Failed to load appointment details. Please try again.',
          logLabel: 'Error loading appointment details',
          onSuccess: data => {
            this.selectedAppointmentForDetails = data as Appointment;
            this.showAppointmentDetailsModal = true;
          },
          onFailure: () => this.onClose(),
          onError: () => this.onClose(),
        }
      );
    } else {
      // If no appointment ID, just close the modal
      this.onClose();
    }
  }

  /**
   * Close the appointment details modal, clear related selection/state, and defer invoking onClose to allow any follow-up modals to open first.
   * @example
   * nAppointmentDetailsModalClose()
   * undefined
   * @returns {void} No return value.
   **/
  onAppointmentDetailsModalClose(): void {
    this.showAppointmentDetailsModal = false;
    this.selectedAppointmentForDetails = null;
    this.createdAppointmentId = '';
    // Defer close so dropdown action emitters can open their own modals first.
    setTimeout(() => {
      const hasFollowUpModalOpen =
        this.isRescheduleModalVisible ||
        this.isMarkCompleteModalVisible ||
        this.isCancelModalVisible ||
        this.isCancellationReasonModalVisible ||
        this.isCancellationSuccessModalVisible ||
        this.isCancellationErrorModalVisible;

      if (!hasFollowUpModalOpen) {
        this.onClose();
      }
    }, 0);
  }

  onMarkCompleteFromDetailsModal(appointment: Appointment): void {
    this.appointmentToMarkComplete = appointment;
    this.isMarkCompleteModalVisible = true;
  }

  onRescheduleFromDetailsModal(appointment: Appointment): void {
    this.appointmentToReschedule = appointment;
    this.isRescheduleModalVisible = true;
  }

  onCancelFromDetailsModal(appointment: Appointment): void {
    this.showAppointmentDetailsModal = false;
    this.appointmentToCancel = appointment;
    this.isCancelModalVisible = true;
  }

  /**
   * Marks the given appointment as completed via the appointment service and updates UI state with success/error messages.
   * @example
   * nMarkCompleteConfirm({ id: 123, /* ... */
  onMarkCompleteConfirm(appointment: Appointment): void {
    const payload = {
      appointment_status: 'completed',
    };

    this.appointmentService.updateAppointment(appointment.id, payload).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: 'Appointment complete!',
          detail: sanitizeErrorMessage('The appointment has been marked complete successfully.'),
        });
        this.isMarkCompleteModalVisible = false;
        this.appointmentToMarkComplete = null;
        this.onAppointmentUpdated();
      },
      error: () => {
        this.isMarkCompleteModalVisible = false;
        this.appointmentToMarkComplete = null;
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: sanitizeErrorMessage('Failed to mark appointment complete. Please try again.'),
        });
      },
    });
  }

  onMarkCompleteModalClose(): void {
    this.isMarkCompleteModalVisible = false;
    this.appointmentToMarkComplete = null;
  }

  onRescheduleModalClose(): void {
    this.isRescheduleModalVisible = false;
    this.appointmentToReschedule = null;
    this.isRescheduleLoading = false;
  }

  /**
   * Reschedules an appointment by calling the appointment service and updating UI state and notifications.
   * @example
   * nRescheduleUpdate({ appointmentId: 'abc123', payload: { date: '2026-03-20T10:00:00Z' } })
   * void
   * @param {{appointmentId: string; payload: any}} {{data}} - Data object containing the appointmentId and the payload to update the appointment with.
   * @returns {{void}} No return value; updates component state, closes the modal, and shows success or error messages.
   **/
  onRescheduleUpdate(data: { appointmentId: string; payload: any }): void {
    this.isRescheduleLoading = true;
    this.appointmentService.updateAppointment(data.appointmentId, data.payload).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: 'Appointment changed!',
          detail: sanitizeErrorMessage('The appointment has been rescheduled successfully.'),
        });
        this.isRescheduleLoading = false;
        this.isRescheduleModalVisible = false;
        this.appointmentToReschedule = null;
        this.onAppointmentUpdated();
      },
      error: () => {
        this.isRescheduleLoading = false;
        this.isRescheduleModalVisible = false;
        this.appointmentToReschedule = null;
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: sanitizeErrorMessage('Failed to reschedule appointment. Please try again.'),
        });
      },
    });
  }

  onCancelConfirm(_appointment: Appointment): void {
    this.isCancelModalVisible = false;
    this.isCancellationReasonModalVisible = true;
  }

  /**
   * Cancels the given appointment using the provided reason, sends the cancellation request to the backend, and updates modal visibility and events based on the response.
   * @example
   * nCancelReasonSubmit({ item: appointmentObj, reason: 'Client requested cancellation' })
   * void
   * @param {{item: Appointment | any; reason: string}} {{data}} - Object containing the appointment (or any) to cancel and the cancellation reason.
   * @returns {{void}} No return value; performs side effects (HTTP request, modal visibility updates, and emits appointmentCreated on success).
   **/
  onCancelReasonSubmit(data: { item: Appointment | any; reason: string }): void {
    const payload = {
      appointment_status: 'cancelled',
      cancellation_reason: data.reason,
      cancellation_date: new Date().toISOString(),
    };
    const item = data.item as Appointment;
    this.appointmentService.cancelAppointment(item.id, payload).subscribe({
      next: () => {
        this.isCancellationReasonModalVisible = false;
        this.isCancellationSuccessModalVisible = true;
        this.appointmentCreated.emit();
      },
      error: () => {
        this.isCancellationReasonModalVisible = false;
        this.isCancellationErrorModalVisible = true;
        this.appointmentToCancel = null;
      },
    });
  }

  onCancelModalClose(): void {
    this.isCancelModalVisible = false;
    this.appointmentToCancel = null;
  }

  onCancellationReasonModalClose(): void {
    this.isCancellationReasonModalVisible = false;
    this.appointmentToCancel = null;
  }

  onCancellationSuccessModalOkay(): void {
    clearCancellationModalsAndAppointment(this);
  }

  onCancellationSuccessModalCreateAnother(): void {
    clearCancellationModalsAndAppointment(this);
    this.onClose();
  }

  onCancellationSuccessModalClose(): void {
    clearCancellationModalsAndAppointment(this);
  }

  onCancellationErrorModalOkay(): void {
    clearCancellationModalsAndAppointment(this);
  }

  onCancellationErrorModalClose(): void {
    clearCancellationModalsAndAppointment(this);
  }

  /**
   * Refresh appointment details for the recently created appointment (if any) and notify the parent to refresh the appointments list.
   * @example
   * nAppointmentUpdated()
   * undefined
   * @param {void} none - This function does not accept any arguments.
   * @returns {void} No return value; performs side effects (fetches appointment details and emits an event).
   **/
  onAppointmentUpdated(): void {
    // Refresh appointment details if needed
    if (this.createdAppointmentId) {
      this.appointmentService.getAppointmentById(this.createdAppointmentId).subscribe({
        next: response => {
          if (response.status && response.data) {
            this.selectedAppointmentForDetails = response.data;
          }
        },
        error: error => {
          console.error('Error refreshing appointment details:', error);
        },
      });
    }
    this.appointmentCreated.emit(); // Notify parent to refresh appointments list
  }

  // Mobile dropdown methods
  /**
   * Update component state to reflect whether the viewport is mobile and attach a resize listener to keep state in sync; closes open dropdowns when switching to desktop.
   * @example
   * heckMobileView()
   * undefined
   * @returns {{void}} No return value.
   **/
  checkMobileView(): void {
    this.isMobile = window.innerWidth < 768;

    // Listen for window resize events
    window.addEventListener('resize', () => {
      this.isMobile = window.innerWidth < 768;
      // Close any open dropdowns when switching to desktop
      if (!this.isMobile) {
        this.openDropdownIndex = -1;
      }
    });
  }

  toggleDropdown(index: number): void {
    if (this.openDropdownIndex === index) {
      this.openDropdownIndex = -1;
    } else {
      this.openDropdownIndex = index;
    }
  }

  closeDropdown(): void {
    this.openDropdownIndex = -1;
  }

  onDropdownAction(action: string, vehicle: any, index: number): void {
    this.closeDropdown();

    if (action === 'edit') {
      this.editVehicle(vehicle, index);
    } else if (action === 'delete') {
      this.deleteVehicle(vehicle, index);
    }
  }

  // Method to reset processing state in child components
  private resetProcessingState(): void {
    // Reset processing state in the customer information form component
    if (this.vehicleDetailsForm) {
      this.vehicleDetailsForm.onProcessingStateChange(false);
    }
  }

  trackByIndex(index: number): number {
    return index;
  }
  trackByValue(_: number, item: any): any {
    return item?.value ?? item;
  }
  trackById(_: number, item: any): any {
    return item?.id ?? item;
  }
  trackByString(_: number, s: any): any {
    return s;
  }
}
